import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Avatar, Tooltip, TextField,
  InputAdornment, CircularProgress, TablePagination,
  Grid, Card, CardContent, CardHeader, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  MenuItem, Switch, FormControlLabel,
  useTheme, useMediaQuery, Pagination,
  ToggleButton, ToggleButtonGroup, alpha
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import WarningIcon from '@mui/icons-material/Warning';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import HealingIcon from '@mui/icons-material/Healing';
import SportsBasketballIcon from '@mui/icons-material/SportsBasketball';
import SchoolIcon from '@mui/icons-material/School';
import GroupsIcon from '@mui/icons-material/Groups';
import api from '../api/axiosConfig';
import { useNavigate } from 'react-router-dom';
import { getMediaBaseUrl } from '../utils/mediaUrl';
import { usePositions } from '../utils/positions';

// Styled Components
const SectionCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: '8px',
  boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(1.5),
    borderRadius: '6px',
    '& .MuiCardContent-root': {
      padding: theme.spacing(2),
      '&:last-child': {
        paddingBottom: theme.spacing(2),
      },
    },
  },
}));

const InfoBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(1),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

// === Sélecteur visuel Pro / Cantera / Tous ===
const PlayerTypeToggle = ({ value, onChange, t }) => {
  const handleChange = (_event, newValue) => {
    if (newValue !== null) onChange(newValue);
  };
  return (
    <ToggleButtonGroup
      value={value}
      exclusive
      onChange={handleChange}
      fullWidth
      sx={{
        height: 56,
        '& .MuiToggleButton-root': {
          textTransform: 'none',
          fontWeight: 600,
          gap: 0.75,
          borderColor: 'rgba(0,0,0,0.23)',
        },
        '& .MuiToggleButton-root.Mui-selected.toggle-all': {
          backgroundColor: alpha('#607d8b', 0.15),
          color: '#37474f',
          borderColor: '#607d8b',
        },
        '& .MuiToggleButton-root.Mui-selected.toggle-pro': {
          backgroundColor: alpha('#ff9800', 0.18),
          color: '#e65100',
          borderColor: '#ff9800',
        },
        '& .MuiToggleButton-root.Mui-selected.toggle-cantera': {
          backgroundColor: alpha('#2e7d32', 0.18),
          color: '#1b5e20',
          borderColor: '#2e7d32',
        },
      }}
    >
      <ToggleButton value="all" className="toggle-all">
        <GroupsIcon fontSize="small" />
        {t('allMedical.all')}
      </ToggleButton>
      <ToggleButton value="pro" className="toggle-pro">
        <SportsBasketballIcon fontSize="small" />
        {t('allMedical.pro')}
      </ToggleButton>
      <ToggleButton value="cantera" className="toggle-cantera">
        <SchoolIcon fontSize="small" />
        {t('allMedical.cantera')}
      </ToggleButton>
    </ToggleButtonGroup>
  );
};

// Mobile Card Component
const MobileMedicalCard = ({ medical, navigate, onEdit, onDelete, t, translateFromDB }) => {
  const theme = useTheme();
  const birthYear = medical.player_date_naissance ? new Date(medical.player_date_naissance).getFullYear() : null;
  const currentAge = calculateCurrentAge(medical.player_date_naissance);
  const shortNat = getShortNationality(medical.player_nationalite);

  return (
    <Card sx={{ mb: 2, boxShadow: theme.shadows[2] }}>
      <CardHeader
        avatar={
          <Avatar src={getPlayerAvatarUrl(medical.id_player)} alt={medical.player_nom} />
        }
        title={
          <Typography 
            onClick={() => navigate(`/players/${medical.id_player}`)}
            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' }}}
          >
            {medical.player_nom}
          </Typography>
        }
        subheader={
          <Box>
            <Typography variant="caption" display="block">
              {new Date(medical.date).toLocaleDateString()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {translateFromDB(medical.player_poste)} • {birthYear && currentAge ? `${birthYear} (${currentAge})` : 'N/A'} • {shortNat}
            </Typography>
          </Box>
        }
        action={
          <Box>
            <IconButton size="small" onClick={() => onEdit(medical)} sx={{ color: '#1976d2' }}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => onDelete(medical)} sx={{ color: '#d32f2f' }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        }
      />
      <CardContent>
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2" component="span">
            <strong>{t('allMedical.team')}: </strong>
          </Typography>
          {/* Si on a des données dans competitions_detail, les afficher en priorité */}
          {medical.player_competitions_detail && medical.player_competitions_detail.length > 0 ? (
            <Box sx={{ display: 'inline-flex', flexDirection: 'column', ml: 1 }}>
              {/* Grouper par équipe */}
              {[...new Set(medical.player_competitions_detail.map(c => c.equipe_nom))].map((equipe, idx) => {
                // Récupérer l'ID de l'équipe et les compétitions associées
                const equipeData = medical.player_competitions_detail.find(c => c.equipe_nom === equipe);
                const competitions = medical.player_competitions_detail
                  .filter(c => c.equipe_nom === equipe)
                  .filter((value, index, self) =>
                    index === self.findIndex((c) => c.competition_id === value.competition_id)
                  );
                const mediaUrl = getMediaBaseUrl();
                const timestamp = Date.now();

                return (
                  <Box key={idx} sx={{ mb: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {/* Logo de l'équipe */}
                      <Box
                        component="img"
                        src={`${mediaUrl}/equipes/${equipeData.equipe_id}.jpg?t=${timestamp}`}
                        alt={equipe}
                        sx={{
                          width: 20,
                          height: 20,
                          objectFit: 'contain',
                          borderRadius: '2px'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {equipe}
                      </Typography>
                    </Box>
                    {/* Logos et noms des compétitions */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', ml: 3 }}>
                      {competitions.map((comp, compIdx) => (
                        <Box key={compIdx} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <Box
                            component="img"
                            src={`${mediaUrl}/competitions/${comp.competition_id}.jpg?t=${timestamp}`}
                            alt={comp.competition_nom}
                            sx={{
                              width: 16,
                              height: 16,
                              objectFit: 'contain',
                              borderRadius: '2px'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          <Typography variant="caption">{comp.competition_nom}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : (
            // Sinon afficher l'ancien système
            <Typography variant="body2" component="span">
              {medical.player_team || 'N/A'}
              {(medical.player_league_1 || medical.player_league_2) && (
                <span style={{ fontSize: '12px', color: '#666' }}>
                  {' '}({[medical.player_league_1, medical.player_league_2].filter(Boolean).join(' - ')})
                </span>
              )}
            </Typography>
          )}
        </Box>
        <Typography variant="body2" gutterBottom>
          <strong>{t('playerCardCreator.medicalForm.injury')}:</strong> {medical.libelle || t('allMedical.notSpecified')}
          {medical.affiche_logo_blessure === 1 && (
            <HealingIcon sx={{ fontSize: 16, ml: 1, verticalAlign: 'middle', color: '#ff9800' }} />
          )}
        </Typography>
        {medical.chirurgie && (
          <Typography variant="body2" gutterBottom>
            <strong>{t('playerCardCreator.medicalForm.surgery')}:</strong> {medical.chirurgie}
          </Typography>
        )}
        {medical.recuperation && (
          <Typography variant="body2" gutterBottom>
            <strong>{t('playerCardCreator.medicalForm.recovery')}:</strong> {medical.recuperation} {/^\d+$/.test(medical.recuperation) ? t('allMedical.days') : ''}
          </Typography>
        )}
        {medical.date_reprise && (
          <Typography variant="body2" gutterBottom>
            <strong>{t('allMedical.returnDate')}:</strong> {new Date(medical.date_reprise).toLocaleDateString()}
          </Typography>
        )}
        {medical.fuente && (
          <Typography variant="body2" gutterBottom>
            <strong>{t('playerCardCreator.medicalForm.source')}:</strong> {medical.fuente}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          {t('allMedical.creator')}: {medical.usuario_nombre || 'N/A'} • {new Date(medical.created_at).toLocaleDateString()}
        </Typography>
      </CardContent>
    </Card>
  );
};

// Helper functions
const getPlayerAvatarUrl = (playerId) => {
  return `${getMediaBaseUrl()}/photos/${playerId}.png?${new Date().getTime()}`;
};

const getUserAvatarUrl = (userId) => {
  return userId
    ? `${getMediaBaseUrl()}/users/${userId}.png?${new Date().getTime()}`
    : '/path/to/default-avatar.png';
};

const getInitials = (prenom, nom) => {
  const firstInitial = prenom ? prenom[0] : '';
  const secondInitial = nom ? nom[0] : '';
  return (firstInitial + secondInitial).toUpperCase();
};

const getAvatarColor = (name) => {
  if (!name) return '#757575';
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash % 360);
  return `hsl(${hue}, 70%, 50%)`;
};

const calculateCurrentAge = (dateNaissance) => {
  if (!dateNaissance) return '';
  const birth = new Date(dateNaissance);
  const today = new Date();
  
  if (isNaN(birth.getTime())) {
    return '';
  }
  
  const diffMs = today - birth;
  const ageInYears = diffMs / (1000 * 60 * 60 * 24 * 365.25);
  
  return ageInYears.toFixed(1);
};

const getShortNationality = (nat) => {
  if (!nat) return '';
  const match = nat.match(/\((.*?)\)/);
  if (match) return match[1];
  return nat.slice(0, 3).toUpperCase();
};

const AllMedicalTable = () => {
  const { t } = useTranslation();
  const { translateFromDB } = usePositions(t);
  const [medicals, setMedicals] = useState([]);
  const [filteredMedicals, setFilteredMedicals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPlayerType, setSelectedPlayerType] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [user, setUser] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentMedical, setCurrentMedical] = useState(null);
  const [editedDate, setEditedDate] = useState('');
  const [editedFuente, setEditedFuente] = useState('');
  const [editedLibelle, setEditedLibelle] = useState('');
  const [editedChirurgie, setEditedChirurgie] = useState('');
  const [editedRecuperation, setEditedRecuperation] = useState('');
  const [editedAfficheLogoBlessure, setEditedAfficheLogoBlessure] = useState(false);
  const [medicalToDelete, setMedicalToDelete] = useState(null);


  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    fetchAllMedicals();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [medicals, searchTerm, selectedPlayerType]);

  const fetchAllMedicals = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/medical/all');
      
      let medicalsData = response.data;
      
      // Filtrar según el rol del usuario
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        const userRole = userData.role?.toLowerCase();
        
        if (userRole === 'admin pro') {
          medicalsData = medicalsData.filter(medical => 
            medical.player_type?.toLowerCase() === 'pro'
          );
        } else if (userRole === 'admin cantera') {
          medicalsData = medicalsData.filter(medical => 
            medical.player_type?.toLowerCase() === 'cantera'
          );
        }
      }
      
      setMedicals(medicalsData);
      setFilteredMedicals(medicalsData);
    } catch (error) {
      console.error("Error al recuperar las fichas medicals:", error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = medicals;

    if (searchTerm) {
      filtered = filtered.filter(medical => {
        const searchLower = searchTerm.toLowerCase();
        return medical.player_nom.toLowerCase().includes(searchLower) ||
               (medical.libelle && medical.libelle.toLowerCase().includes(searchLower)) ||
               (medical.fuente && medical.fuente.toLowerCase().includes(searchLower));
      });
    }

    if (selectedPlayerType && selectedPlayerType !== 'all') {
      filtered = filtered.filter(
        medical => medical.player_type?.toLowerCase() === selectedPlayerType
      );
    }

    setFilteredMedicals(filtered);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeMobilePage = (event, newValue) => {
    setPage(newValue - 1);
  };

  const navigateToPlayer = (playerId) => {
    navigate(`/players/${playerId}`);
  };

  const handleEditClick = (medicalRow) => {
    setCurrentMedical(medicalRow);
    const mainDateStr = medicalRow.date
      ? new Date(medicalRow.date).toISOString().split('T')[0]
      : '';
    setEditedDate(mainDateStr);
    setEditedFuente(medicalRow.fuente || '');
    setEditedLibelle(medicalRow.libelle || '');
    setEditedChirurgie(medicalRow.chirurgie || '');
    setEditedRecuperation(medicalRow.recuperation || '');
    setEditedAfficheLogoBlessure(Boolean(medicalRow.affiche_logo_blessure));
    setOpenEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!currentMedical) return;

    const playerType = currentMedical.player_type?.toLowerCase();
    const isPro = playerType === 'pro';

    const updateData = {
      date: editedDate,
      idnuser: currentMedical.idnuser,
      id_player: currentMedical.id_player,
      fuente: editedFuente,
      libelle: editedLibelle,
      chirurgie: editedChirurgie,
      recuperation: editedRecuperation,
      affiche_logo_blessure: isPro ? (editedAfficheLogoBlessure ? 1 : 0) : 0
    };

    try {
      await api.put(`/api/medical/${currentMedical.id}`, updateData);

      fetchAllMedicals();
      setOpenEditDialog(false);
      setCurrentMedical(null);
    } catch (error) {
      console.error('Error al actualizar la ficha médica:', error);
      alert('Error al actualizar la ficha médica.');
    }
  };

  const handleDeleteClick = (medicalRow) => {
    setMedicalToDelete(medicalRow);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!medicalToDelete) return;

    try {
      await api.delete(`/api/medical/${medicalToDelete.id}`);

      fetchAllMedicals();
      setOpenDeleteDialog(false);
      setMedicalToDelete(null);
    } catch (error) {
      console.error('Error al eliminar la ficha médica:', error);
      alert('Error al eliminar la ficha médica.');
    }
  };


  const totalResults = filteredMedicals.length;
  const paginatedData = React.useMemo(() => {
    if (rowsPerPage <= 0) {
      return filteredMedicals;
    }
    return filteredMedicals.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredMedicals, page, rowsPerPage]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', pb: isMobile ? 7 : 0 }}>
      {isMobile ? (
        <>
          {/* Vista móvil */}
          <Box
            sx={{
              mx: 2,
              mt: 2,
              mb: 1,
              display: 'flex',
              alignItems: 'center',
              p: 2,
              bgcolor: theme.palette.primary.main,
              color: 'white',
              borderRadius: 1,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            <Box
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '50%',
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
                fontWeight: 'bold',
                fontSize: '1.1rem',
              }}
            >
              {totalResults}
            </Box>
            <Typography variant="subtitle1">
              {totalResults === 1 ? t('allMedical.medicalFound') : t('allMedical.medicalsFound')}
            </Typography>
          </Box>

          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder={t('allMedical.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
              sx={{ mb: 2 }}
            />

            {user?.role?.toLowerCase() === 'super admin' && (
              <Box sx={{ mb: 2 }}>
                <PlayerTypeToggle
                  value={selectedPlayerType}
                  onChange={(val) => setSelectedPlayerType(val)}
                  t={t}
                />
              </Box>
            )}

            {paginatedData.map((medical) => (
              <MobileMedicalCard
                key={medical.id}
                medical={medical}
                navigate={navigate}
                onEdit={handleEditClick}
                onDelete={handleDeleteClick}
                t={t}
                translateFromDB={translateFromDB}
              />
            ))}
          </Box>

          {/* Paginación móvil */}
          {rowsPerPage !== -1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <Pagination
                color="primary"
                count={Math.ceil(totalResults / rowsPerPage)}
                page={page + 1}
                onChange={handleChangeMobilePage}
              />
            </Box>
          )}
        </>
      ) : (
        <>
          {/* Vista escritorio */}
          <Grid container spacing={2} marginBottom={2}>
            <Grid item xs={12} sm={user?.role?.toLowerCase() === 'super admin' ? 8 : 12}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder={t('allMedical.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            {user?.role?.toLowerCase() === 'super admin' && (
              <Grid item xs={12} sm={4}>
                <PlayerTypeToggle
                  value={selectedPlayerType}
                  onChange={(val) => setSelectedPlayerType(val)}
                  t={t}
                />
              </Grid>
            )}
          </Grid>

          <Box
            sx={{
              mb: 3,
              display: 'flex',
              alignItems: 'center',
              p: 2,
              bgcolor: theme.palette.primary.main,
              color: 'white',
              borderRadius: 1,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            <Box
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '50%',
                width: 48,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
                fontWeight: 'bold',
                fontSize: '1.2rem',
              }}
            >
              {totalResults}
            </Box>
            <Typography variant="h6">
              {totalResults === 1 ? t('allMedical.medicalFound') : t('allMedical.medicalsFound')}
            </Typography>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('allMedical.creator')}</TableCell>
                  <TableCell align="center">{t('common.date')}</TableCell>
                  <TableCell>{t('allMedical.player')}</TableCell>
                  <TableCell align="center">{t('allMedical.position')}</TableCell>
                  <TableCell align="center">{t('allMedical.age')}</TableCell>
                  <TableCell align="center">{t('allMedical.nationality')}</TableCell>
                  <TableCell align="center">{t('allMedical.team')}</TableCell>
                  <TableCell>{t('playerCardCreator.medicalForm.injury')}</TableCell>
                  <TableCell align="center">{t('playerCardCreator.medicalForm.surgery')}</TableCell>
                  <TableCell align="center">{t('playerCardCreator.medicalForm.recovery')}</TableCell>
                  <TableCell align="center">{t('allMedical.returnDate')}</TableCell>
                  <TableCell>{t('playerCardCreator.medicalForm.source')}</TableCell>
                  <TableCell align="center">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((row) => {
                  const birthYear = row.player_date_naissance ? new Date(row.player_date_naissance).getFullYear() : null;
                  const currentAge = calculateCurrentAge(row.player_date_naissance);
                  const shortNat = getShortNationality(row.player_nationalite);
                  
                  return (
                    <TableRow key={row.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Tooltip
                            arrow
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  bgcolor: 'white',
                                  '& .MuiTooltip-arrow': { color: 'white' },
                                  boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
                                },
                              },
                            }}
                            title={
                              <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar
                                  src={getUserAvatarUrl(row.usuario_id)}
                                  sx={{ width: 50, height: 50 }}
                                />
                                <Box>
                                  <Typography
                                    variant="body1"
                                    sx={{ fontWeight: 'bold', color: 'black' }}
                                  >
                                    {row.usuario_prenom} {row.usuario_nom}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.7)' }}>
                                    {row.usuario_nombre}
                                  </Typography>
                                </Box>
                              </Box>
                            }
                          >
                            <Avatar
                              sx={{
                                mr: 1,
                                bgcolor: getAvatarColor(
                                  row.usuario_nom + row.usuario_prenom
                                ),
                              }}
                            >
                              {getInitials(row.usuario_prenom, row.usuario_nom)}
                            </Avatar>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        {new Date(row.date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar
                            src={getPlayerAvatarUrl(row.id_player)}
                            alt={row.player_nom}
                            sx={{ mr: 1, cursor: 'pointer' }}
                            onClick={() => navigateToPlayer(row.id_player)}
                          />
                          <Typography 
                            variant="body2"
                            sx={{ 
                              cursor: 'pointer',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                            onClick={() => navigateToPlayer(row.id_player)}
                          >
                            {row.player_nom}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">{translateFromDB(row.player_poste) || '-'}</TableCell>
                      <TableCell align="center">
                        {birthYear && currentAge ? `${birthYear} (${currentAge})` : '-'}
                      </TableCell>
                      <TableCell align="center">{shortNat || '-'}</TableCell>
                      <TableCell align="center">
                        <Box>
                          {/* Si on a des données dans competitions_detail, les afficher en priorité */}
                          {row.player_competitions_detail && row.player_competitions_detail.length > 0 ? (
                            <>
                              {/* Grouper par équipe */}
                              {[...new Set(row.player_competitions_detail.map(c => c.equipe_nom))].map((equipe, idx) => {
                                // Récupérer l'ID de l'équipe et les compétitions associées
                                const equipeData = row.player_competitions_detail.find(c => c.equipe_nom === equipe);
                                const competitions = row.player_competitions_detail
                                  .filter(c => c.equipe_nom === equipe)
                                  .filter((value, index, self) =>
                                    index === self.findIndex((c) => c.competition_id === value.competition_id)
                                  );
                                const mediaUrl = getMediaBaseUrl();
                                const timestamp = Date.now();

                                return (
                                  <Box key={idx} sx={{ mb: 0.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                      {/* Logo de l'équipe */}
                                      <Box
                                        component="img"
                                        src={`${mediaUrl}/equipes/${equipeData.equipe_id}.jpg?t=${timestamp}`}
                                        alt={equipe}
                                        sx={{
                                          width: 16,
                                          height: 16,
                                          objectFit: 'contain',
                                          borderRadius: '2px'
                                        }}
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                        }}
                                      />
                                      <div style={{ fontWeight: 'bold', fontSize: '12px' }}>{equipe}</div>
                                    </Box>
                                    {/* Logos et noms des compétitions */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.3, flexWrap: 'wrap', mt: 0.3 }}>
                                      {competitions.map((comp, compIdx) => (
                                        <Box key={compIdx} sx={{ display: 'flex', alignItems: 'center', gap: 0.2 }}>
                                          <Box
                                            component="img"
                                            src={`${mediaUrl}/competitions/${comp.competition_id}.jpg?t=${timestamp}`}
                                            alt={comp.competition_nom}
                                            sx={{
                                              width: 14,
                                              height: 14,
                                              objectFit: 'contain',
                                              borderRadius: '2px'
                                            }}
                                            onError={(e) => {
                                              e.target.style.display = 'none';
                                            }}
                                          />
                                          <span style={{ fontSize: '10px' }}>{comp.competition_nom}</span>
                                        </Box>
                                      ))}
                                    </Box>
                                  </Box>
                                );
                              })}
                            </>
                          ) : (
                            // Sinon afficher l'ancien système (team et optionnellement les ligues)
                            <Box>
                              {row.player_team ? (
                                <div style={{ fontSize: '12px' }}>{row.player_team}</div>
                              ) : (
                                <div style={{ fontSize: '12px' }}>-</div>
                              )}
                              {(row.player_league_1 || row.player_league_2) && (
                                <div style={{ fontSize: '10px', color: '#666' }}>
                                  {[row.player_league_1, row.player_league_2].filter(Boolean).join(' - ')}
                                </div>
                              )}
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {row.libelle || t('allMedical.notSpecified')}
                          {row.affiche_logo_blessure === 1 && (
                            <Tooltip title={t('allMedical.showsInjuryLogo')}>
                              <HealingIcon sx={{ fontSize: 16, color: '#ff9800' }} />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">{row.chirurgie || '-'}</TableCell>
                      <TableCell align="center">
                        {row.recuperation ? `${row.recuperation}${/^\d+$/.test(row.recuperation) ? ` ${t('allMedical.days')}` : ''}` : '-'}
                      </TableCell>
                      <TableCell align="center">
                        {row.date_reprise ? new Date(row.date_reprise).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell>{row.fuente || '-'}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleEditClick(row)}
                          sx={{ color: '#1976d2' }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(row)}
                          sx={{ color: '#d32f2f' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {/* Paginación escritorio */}
            <TablePagination
              rowsPerPageOptions={[25, 50, 100, { label: t('common.all'), value: -1 }]}
              component="div"
              count={totalResults}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage={t('allMedical.medicalsPerPage')}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t('allMedical.of')} ${count}`}
            />
          </TableContainer>
        </>
      )}
    

      {/* Dialog para editar */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{t('allMedical.editMedical')}</DialogTitle>
        <DialogContent>
          {currentMedical && (
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label={t('playerCardCreator.medicalForm.date')}
                type="date"
                value={editedDate}
                onChange={(e) => setEditedDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />

              {currentMedical.player_type === 'Pro' && (
                <TextField
                  fullWidth
                  label={t('playerCardCreator.medicalForm.source')}
                  value={editedFuente}
                  onChange={(e) => setEditedFuente(e.target.value)}
                  sx={{ mb: 2 }}
                />
              )}

              <TextField
                fullWidth
                label={t('playerCardCreator.medicalForm.injury')}
                value={editedLibelle}
                onChange={(e) => setEditedLibelle(e.target.value)}
                sx={{ mb: 2 }}
              />

              <TextField
                select
                fullWidth
                label={t('playerCardCreator.medicalForm.surgery')}
                value={editedChirurgie}
                onChange={(e) => setEditedChirurgie(e.target.value)}
                sx={{ mb: 2 }}
              >
                <MenuItem value="">-</MenuItem>
                <MenuItem value="no">{t('playerCardCreator.medicalForm.no')}</MenuItem>
                <MenuItem value="si">{t('playerCardCreator.medicalForm.yes')}</MenuItem>
              </TextField>

              {currentMedical.player_type === 'Pro' ? (
                <TextField
                  select
                  fullWidth
                  label={t('playerCardCreator.medicalForm.recovery')}
                  value={editedRecuperation}
                  onChange={(e) => setEditedRecuperation(e.target.value)}
                  sx={{ mb: 2 }}
                >
                  <MenuItem value="">{t('playerCardCreator.medicalForm.select')}</MenuItem>
                  <MenuItem value="menos de una semana">{t('playerCardCreator.medicalForm.recoveryOptions.lessThanWeek')}</MenuItem>
                  <MenuItem value="2 semanas">{t('playerCardCreator.medicalForm.recoveryOptions.twoWeeks')}</MenuItem>
                  <MenuItem value="3 semanas">{t('playerCardCreator.medicalForm.recoveryOptions.threeWeeks')}</MenuItem>
                  <MenuItem value="4 semanas">{t('playerCardCreator.medicalForm.recoveryOptions.fourWeeks')}</MenuItem>
                  <MenuItem value="1-2 meses">{t('playerCardCreator.medicalForm.recoveryOptions.oneToTwoMonths')}</MenuItem>
                  <MenuItem value="2-3 meses">{t('playerCardCreator.medicalForm.recoveryOptions.twoToThreeMonths')}</MenuItem>
                  <MenuItem value="4-5 meses">{t('playerCardCreator.medicalForm.recoveryOptions.fourToFiveMonths')}</MenuItem>
                  <MenuItem value="5-6 meses">{t('playerCardCreator.medicalForm.recoveryOptions.fiveToSixMonths')}</MenuItem>
                  <MenuItem value="6 meses - 1 año">{t('playerCardCreator.medicalForm.recoveryOptions.sixMonthsToYear')}</MenuItem>
                  <MenuItem value="más de 1 año">{t('playerCardCreator.medicalForm.recoveryOptions.moreThanYear')}</MenuItem>
                </TextField>
              ) : (
                currentMedical.player_type === 'Cantera' && (
                  <TextField
                    fullWidth
                    label={t('playerCardCreator.medicalForm.recoveryDays')}
                    type="number"
                    value={editedRecuperation}
                    onChange={(e) => setEditedRecuperation(e.target.value)}
                    InputProps={{ inputProps: { min: 0 } }}
                    sx={{ mb: 2 }}
                  />
                )
              )}

              {currentMedical.player_type === 'Pro' && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={editedAfficheLogoBlessure}
                      onChange={(e) => setEditedAfficheLogoBlessure(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      {t('playerCardCreator.medicalForm.showInjuryLogo')} <HealingIcon sx={{ ml: 1 }} />
                    </span>
                  }
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleSaveEdit} variant="contained" color="primary">
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para confirmar eliminación */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>{t('allMedical.deleteMedical')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('allMedical.confirmDeleteMessage')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    
    </Box>
  );
};

export default AllMedicalTable;